import { useCallback, useEffect, useMemo, useState } from "react";
import { Flex, Text } from "@radix-ui/themes";
import { params } from "@/params";
import {
  defaultChatSettings,
  defaultTheme,
} from "@/components/quack-chat/QuackChat";
import type {
  LayoutConfiguration,
  QuackChatConfigResponse,
} from "@/components/quack-chat/QuackChatConfig.model";
import type { ChatSettingsV2 } from "@/components/quack-chat/core/entities/ChatSettingsV2";
import type { TenantUserTicket } from "@/components/quack-chat/core/entities/TenantUserTicket";
import { getAuthorInitials } from "@/components/quack-chat/core/entities/Message";
import { Input } from "@/components/quack-chat/core/parts/Input";
import { MessageAvatarSVG } from "@/components/quack-chat/core/parts/loadSVG";
import { QuackWidgetCard } from "@/components/quack-chat/core/parts/QuackWidgetCard";
import { QuackChatWidgetTheme } from "@/components/quack-chat/core/parts/QuackChatWidgetTheme";
import { TicketBriff } from "@/components/quack-chat/core/parts/ticket/TicketBriff";
import { TicketList } from "@/components/quack-chat/core/parts/ticket/TicketList";
import { TicketView } from "@/components/quack-chat/core/parts/ticket/TicketView";
import "@/components/quack-chat/core/styles/index.scss";
import styles from "./QuackchatChannelLayoutPreview.module.scss";

type HomeView = "home" | "ticket-list" | "ticket-view";

type PreviewTicketComment = {
  id: string;
  body: string;
  createdAt: string;
  attachmentUrls?: string[];
  agent: boolean;
};

const previewTickets: TenantUserTicket[] = [
  {
    id: "TCK-1042",
    createdAt: "2024-01-12T09:00:00Z",
    updatedAt: "2024-01-12T12:30:00Z",
    subject: "Unable to update billing info",
    status: "open",
    publicCollaborators: [{ id: "agent-1", name: "Jamie Park" }],
  },
  {
    id: "TCK-1043",
    createdAt: "2024-01-10T14:00:00Z",
    updatedAt: "2024-01-11T11:00:00Z",
    subject: "App shows blank screen after login",
    status: "pending",
    publicCollaborators: [{ id: "agent-2", name: "Sam Lee" }],
  },
  {
    id: "TCK-1044",
    createdAt: "2024-01-09T08:00:00Z",
    updatedAt: "2024-01-09T09:30:00Z",
    subject: "Need invoice copy for January",
    status: "open",
    publicCollaborators: [{ id: "agent-3", name: "Taylor Reed" }],
  },
];

const previewComments: PreviewTicketComment[] = [
  {
    id: "comment-1",
    body: "Thanks for flagging this. We're reviewing your billing profile now.",
    createdAt: "2024-01-12T10:00:00Z",
    agent: true,
  },
  {
    id: "comment-2",
    body: "Appreciate it. I can share screenshots if helpful.",
    createdAt: "2024-01-12T10:10:00Z",
    agent: false,
  },
  {
    id: "comment-3",
    body: "Screenshots would be great. We'll keep you posted here.",
    createdAt: "2024-01-12T10:12:00Z",
    agent: true,
  },
];

const previewUserName = "John Doe";
const previewUserEmail = "john@quack.ai";

interface QuackchatChannelLayoutPreviewProps {
  configuration: QuackChatConfigResponse;
  layout: LayoutConfiguration;
}

export function QuackchatChannelLayoutPreview({
  configuration,
  layout,
}: QuackchatChannelLayoutPreviewProps) {
  const [view, setView] = useState<HomeView>("home");
  const [selectedTicket, setSelectedTicket] =
    useState<TenantUserTicket | null>(null);
  const [backToHome, setBackToHome] = useState<HomeView>("home");

  const resolvedLayout = useMemo(() => layout, [layout]);

  const {
    greetingEnabled,
    askAiInputEnabled,
    supportTicketsEnabled,
    endUserTicketName,
    ticketStatusesToDisplay,
    greetingTitle,
    fallbackUserName,
    askAiPlaceholder,
    supportTicketsStyle,
  } = useMemo(() => {
    const greetingConfig = resolvedLayout.greeting ?? {
      enabled: true,
      title: "Hello {USER}",
      fallbackUserName: "there",
    };
    const askAiInputConfig = resolvedLayout.askAiInput ?? {
      enabled: true,
      placeholder: defaultChatSettings.placeholder,
    };
    const supportTicketsConfig = resolvedLayout.supportTickets ?? {
      enabled: true,
      statusesToDisplayOnThePage: [],
      endUserTicketName: "Support Cases",
      allowUserReplyToTicket: false,
      style: "list",
    };

    return {
      greetingEnabled: greetingConfig.enabled !== false,
      askAiInputEnabled: askAiInputConfig.enabled !== false,
      supportTicketsEnabled: supportTicketsConfig.enabled !== false,
      endUserTicketName: supportTicketsConfig.endUserTicketName ?? "Support Cases",
      ticketStatusesToDisplay:
        supportTicketsConfig.statusesToDisplayOnThePage ?? [],
      greetingTitle: greetingConfig.title ?? "Hello {USER}",
      fallbackUserName: greetingConfig.fallbackUserName ?? "there",
      askAiPlaceholder:
        askAiInputConfig.placeholder ?? defaultChatSettings.placeholder,
      supportTicketsStyle: supportTicketsConfig.style ?? "list",
    };
  }, [resolvedLayout]);

  const resolvedUserName = useMemo(() => {
    if (previewUserName.trim().length > 0) {
      return previewUserName;
    }

    return fallbackUserName;
  }, [fallbackUserName]);

  const normalizedUserName = useMemo(() => {
    if (resolvedUserName.includes("@")) {
      return resolvedUserName.split("@")[0];
    }

    return resolvedUserName;
  }, [resolvedUserName]);

  const greetingText = useMemo(() => {
    return greetingTitle.split("{USER}").join(normalizedUserName);
  }, [greetingTitle, normalizedUserName]);

  const themeVars = useMemo<ChatSettingsV2["theme"]>(() => {
    const theme = {
      ...defaultTheme,
      ...(configuration.chatSettings.theme ?? {}),
    };

    return {
      userBackground: theme.userBackground,
      userText: theme.userText,
      agentBackground: theme.agentBackground,
      agentText: theme.agentText,
      bgSendButton: theme.bgSendButton,
      textSendButton: theme.textSendButton,
      bgSecondaryButton: theme.bgSecondaryButton,
      textSecondaryButton: theme.textSecondaryButton,
    };
  }, [configuration.chatSettings.theme]);

  const chatSettings = useMemo<ChatSettingsV2>(() => {
    const {
      customSVG,
      closeSVG,
      avatarSVG,
      userAvatarSVG,
      iconSize,
      svgRadius,
      position,
      name,
      quackAgentName,
      placeholder,
      poweredByLine,
      csatType,
      allowCsatComment,
      csatCommentText,
      allowUploadFileBeforeEscalation,
      allowNewChat,
      allowCloseChat,
      showCloseChatConfirmation,
      fallbackSupportEmail,
      errorFallbackContent,
      helpCenterLink,
      enableNewMessageSound,
    } = configuration.chatSettings;

    return {
      name: name ?? defaultChatSettings.name,
      quackAgentName: quackAgentName ?? defaultChatSettings.quackAgentName,
      theme: themeVars,
      customSVG: customSVG
        ? `${params.SDK_CONFIG}${customSVG}`
        : defaultChatSettings.customSVG,
      ...(closeSVG ? { closeSVG: `${params.SDK_CONFIG}${closeSVG}` } : {}),
      ...(avatarSVG ? { avatarSVG: `${params.SDK_CONFIG}${avatarSVG}` } : {}),
      ...(userAvatarSVG
        ? { userAvatarSVG: `${params.SDK_CONFIG}${userAvatarSVG}` }
        : {}),
      iconSize: iconSize ?? defaultChatSettings.iconSize,
      svgRadius,
      position,
      poweredByLine,
      placeholder: placeholder ?? defaultChatSettings.placeholder,
      csatType: csatType ?? defaultChatSettings.csatType,
      allowCsatComment,
      csatCommentText,
      allowUploadFileBeforeEscalation,
      allowNewChat,
      allowCloseChat,
      showCloseChatConfirmation,
      fallbackSupportEmail,
      errorFallbackContent,
      helpCenterLink,
      enableNewMessageSound,
      layout: resolvedLayout,
    };
  }, [configuration.chatSettings, resolvedLayout, themeVars]);

  const handleStartNewChat = useCallback(async () => {
    return;
  }, []);

  const handleUploadFile = useCallback(async () => {
    return;
  }, []);

  const handleShowAllTickets = useCallback(() => {
    if (!supportTicketsEnabled) {
      return;
    }

    setView("ticket-list");
  }, [supportTicketsEnabled]);

  const handleShowTicket = useCallback(
    (ticket: TenantUserTicket) => {
      if (!supportTicketsEnabled) {
        return;
      }

      setBackToHome(view);
      setView("ticket-view");
      setSelectedTicket(ticket);
    },
    [supportTicketsEnabled, view]
  );

  const handleBackFromTicketView = useCallback(() => {
    setView(backToHome);
    setSelectedTicket(null);
  }, [backToHome]);

  const handleBackFromTicketList = useCallback(() => {
    setView("home");
  }, []);

  useEffect(() => {
    if (!supportTicketsEnabled && view !== "home") {
      setView("home");
      setSelectedTicket(null);
    }
  }, [supportTicketsEnabled, view]);

  const isHomeEnabled = useMemo(() => resolvedLayout.enabled, [resolvedLayout]);

  return (
    <div className={styles.Preview}>
      <QuackChatWidgetTheme themeVars={chatSettings.theme}>
        <div className={"qc-Chat__Widget_Popover"}>
          {!isHomeEnabled && (
            <QuackWidgetCard settings={chatSettings}>
              <Flex
                direction="column"
                gap="2"
                p="4"
                align="center"
                justify="center"
                className={styles.DisabledState}
              >
                <Text size="2" color="gray" weight="medium" highContrast>
                  Home page is disabled
                </Text>
                <Text size="1" color="gray">
                  Enable it to preview the layout.
                </Text>
              </Flex>
            </QuackWidgetCard>
          )}

          {isHomeEnabled && view === "ticket-view" && selectedTicket && (
            <QuackWidgetCard settings={chatSettings}>
              <TicketView
                settings={chatSettings}
                ticket={selectedTicket}
                onBack={handleBackFromTicketView}
                comments={previewComments}
                userEmail={previewUserEmail}
              />
            </QuackWidgetCard>
          )}

          {isHomeEnabled && view === "ticket-list" && (
            <QuackWidgetCard settings={chatSettings}>
              <TicketList
                tickets={previewTickets}
                onSelectTicket={handleShowTicket}
                onBack={handleBackFromTicketList}
                statusesToDisplayOnThePage={ticketStatusesToDisplay}
                endUserTicketName={endUserTicketName}
              />
            </QuackWidgetCard>
          )}

          {isHomeEnabled && view === "home" && (
            <QuackWidgetCard settings={chatSettings}>
              <Flex flexGrow="1" p="4" direction="column" gap="4">
                <Flex justify="between" gap="2">
                  <span />
                  <div className={styles.UserAvatar}>
                    <MessageAvatarSVG
                      kind="user"
                      initials={getAuthorInitials(previewUserName) ?? "U"}
                      radius={chatSettings.svgRadius}
                      alt="User Avatar"
                    />
                  </div>
                </Flex>

                <Flex direction="column" gap="5" pt="6">
                  {greetingEnabled && (
                    <Text
                      size="5"
                      color="gray"
                      weight="medium"
                      highContrast
                      align="center"
                    >
                      {greetingText}
                    </Text>
                  )}

                  {askAiInputEnabled && (
                    <div className={styles.HomeInput}>
                      <Input
                        allowUploadFile={false}
                        placeholder={askAiPlaceholder}
                        disabled={false}
                        sendMessage={handleStartNewChat}
                        uploadFile={handleUploadFile}
                      />
                    </div>
                  )}
                </Flex>

                {supportTicketsEnabled && (
                  <Flex direction="column" className={styles.TicketsPreview}>
                    <TicketBriff
                      style={supportTicketsStyle}
                      tickets={previewTickets}
                      isLoading={false}
                      onSelectTicket={handleShowTicket}
                      onShowAllTickets={handleShowAllTickets}
                      statusesToDisplayOnThePage={ticketStatusesToDisplay}
                      endUserTicketName={endUserTicketName}
                    />
                  </Flex>
                )}
              </Flex>
            </QuackWidgetCard>
          )}
        </div>
      </QuackChatWidgetTheme>
    </div>
  );
}
